class Tabs {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    this.root.addEventListener('click', this.#handleClick);
    window.addEventListener('hashchange', this.#handleHashChange);
    this.#syncActiveTab();
  }

  get #tabs() {
    return Array.from(this.root.querySelectorAll('[data-tab-button]')).filter((tab) => tab instanceof HTMLButtonElement);
  }

  get #panels() {
    return Array.from(this.root.querySelectorAll('[data-tab-body]')).filter((panel) => panel instanceof HTMLElement);
  }

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-tab-button]') : null;
    if (!(target instanceof HTMLButtonElement) || target.closest('[data-tabs]') !== this.root) return;

    this.#activateTab(target, true);
  };

  #handleHashChange = () => {
    const activeTab = this.#tabFromHash;
    if (activeTab) this.#activateTab(activeTab);
  };

  get #tabFromHash() {
    // Hashes can be compound (e.g. `orders#OrderNumber-123` deep-links to a
    // specific order within the Orders tab); the first segment is the tab.
    const hash = decodeURIComponent(window.location.hash.slice(1)).split('#')[0];
    if (!hash) return undefined;

    return this.#tabs.find((tab) => this.#getTabHash(tab) === hash);
  }

  #syncActiveTab() {
    const tabs = this.#tabs;
    const activeTab = this.#tabFromHash ?? tabs.find((tab) => tab.classList.contains('active')) ?? tabs[0];
    if (activeTab) this.#activateTab(activeTab);
  }

  /**
   * @param {HTMLButtonElement} tab
   */
  #getTabHash(tab) {
    return tab.dataset.tabHash ?? tab.dataset.id;
  }

  /**
   * @param {HTMLButtonElement} activeTab
   */
  #updateHash(activeTab) {
    const tabHash = this.#getTabHash(activeTab);
    if (!tabHash) return;

    const url = new URL(window.location.href);
    url.hash = tabHash;
    history.replaceState(null, '', url);
  }

  /**
   * @param {HTMLButtonElement} activeTab
   * @param {boolean} updateHash
   */
  #activateTab(activeTab, updateHash = false) {
    const tabs = this.#tabs;
    const panels = this.#panels;
    const activeId = activeTab.dataset.id;

    for (const tab of tabs) {
      const isActive = tab === activeTab;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    }

    for (const panel of panels) {
      panel.hidden = panel.dataset.id !== activeId;
      if (panel.hidden) pauseDetailVideos(panel);
    }

    const indicatorContainer = activeTab.closest('.inline-tab-navigation');
    if (indicatorContainer instanceof HTMLElement) positionTabIndicator(indicatorContainer);

    if (updateHash) this.#updateHash(activeTab);
  }
}

class ScopedTabs {
  /**
   * @param {string} scope
   */
  constructor(scope) {
    this.scope = scope;
    this.defaultTabId = this.#defaultTabId;
    document.addEventListener('click', this.#handleClick);
    window.addEventListener('hashchange', this.#handleHashChange);
    this.#syncActiveTab();
  }

  get #tabs() {
    return Array.from(document.querySelectorAll(`[data-tab-scope="${this.scope}"][data-tab-button]`)).filter(
      (tab) => tab instanceof HTMLButtonElement
    );
  }

  get #panels() {
    return Array.from(document.querySelectorAll(`[data-tab-scope="${this.scope}"][data-tab-body]`)).filter(
      (panel) => panel instanceof HTMLElement
    );
  }

  get #defaultTabId() {
    const defaultTab = document.querySelector(
      `[data-tab-scope="${this.scope}"][data-tab-button][data-tab-default]`
    );
    if (defaultTab instanceof HTMLButtonElement && defaultTab.dataset.id) {
      return defaultTab.dataset.id;
    }

    return this.#tabs[0]?.dataset.id;
  }

  get #defaultTab() {
    const tabs = this.#tabs;
    return tabs.find((tab) => tab.dataset.id === this.defaultTabId) ?? tabs[0];
  }

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-tab-button]') : null;
    if (!(target instanceof HTMLButtonElement) || target.dataset.tabScope !== this.scope) return;

    this.#activateTab(target, true);
  };

  #handleHashChange = () => {
    this.#syncActiveTab();
  };

  get #tabFromHash() {
    const hash = decodeURIComponent(window.location.hash.slice(1));
    if (!hash || hash === this.defaultTabId) return undefined;

    return this.#tabs.find((tab) => this.#getTabHash(tab) === hash);
  }

  #syncActiveTab() {
    const hash = decodeURIComponent(window.location.hash.slice(1));
    if (hash === this.defaultTabId) {
      this.#clearHash();
    }

    const activeTab = this.#tabFromHash ?? this.#defaultTab;
    if (activeTab) this.#activateTab(activeTab);
  }

  /**
   * @param {HTMLButtonElement} tab
   */
  #getTabHash(tab) {
    return tab.dataset.tabHash ?? tab.dataset.id;
  }

  #clearHash() {
    if (!window.location.hash) return;

    const url = new URL(window.location.href);
    url.hash = '';
    history.replaceState(null, '', url);
  }

  /**
   * @param {HTMLButtonElement} activeTab
   */
  #updateHash(activeTab) {
    const tabHash = this.#getTabHash(activeTab);
    if (!tabHash || tabHash === this.defaultTabId) return;

    const url = new URL(window.location.href);
    url.hash = tabHash;
    history.replaceState(null, '', url);
  }

  /**
   * @param {HTMLButtonElement} activeTab
   * @param {boolean} updateHash
   */
  #activateTab(activeTab, updateHash = false) {
    const tabs = this.#tabs;
    const panels = this.#panels;
    const activeId = activeTab.dataset.id;

    for (const tab of tabs) {
      const isActive = tab === activeTab;
      tab.classList.toggle('active', isActive);
      tab.classList.toggle('tab-item-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    }

    for (const panel of panels) {
      panel.hidden = panel.dataset.id !== activeId;
    }

    const indicatorContainer = activeTab.closest('.inline-tab-navigation');
    if (indicatorContainer instanceof HTMLElement) positionTabIndicator(indicatorContainer);

    updateTabItemCount();

    if (!updateHash) return;

    if (activeId === this.defaultTabId) {
      this.#clearHash();
      return;
    }

    this.#updateHash(activeTab);
  }
}

// Tablists that carry a sliding indicator: the inline tab navigation and the
// predictive-search type filter. They share the indicator element and CSS.
const TAB_INDICATOR_CONTAINER_SELECTOR = '.inline-tab-navigation, .predictive-search-form__types';
const TAB_TRIGGER_SELECTOR = '.inline-tab-navigation-trigger, .search__type';
const TAB_ACTIVE_SELECTOR =
  '.inline-tab-navigation-trigger.active, .inline-tab-navigation-trigger.inline-tab-active, .search__type.search__type-active';

/**
 * Positions the sliding indicator (`.inline-tab-navigation-indicator`) flush to
 * the left edge of the active trigger and matches its width. Measured with
 * getBoundingClientRect relative to the container so it stays correct even when
 * the tablist is scrolled horizontally. The CSS transition animates the move.
 * @param {Element} container - The tablist (`.inline-tab-navigation` or `.predictive-search-form__types`).
 * @param {{ animate?: boolean, scroll?: boolean }} [options] - Set animate:false for
 *   initial placement / resize so the indicator snaps instead of sliding from 0.
 *   `scroll` (default: same as `animate`) also scrolls the active trigger into view
 *   when the tablist overflows — enabled on activation, off for resize/init so a
 *   layout change never hijacks the user's scroll position.
 */
export function positionTabIndicator(container, { animate = true, scroll = animate } = {}) {
  if (!(container instanceof HTMLElement)) return;

  const indicator = container.querySelector(':scope > .inline-tab-navigation-indicator');
  if (!(indicator instanceof HTMLElement)) return;

  const active =
    container.querySelector(TAB_ACTIVE_SELECTOR) ?? container.querySelector(TAB_TRIGGER_SELECTOR);

  if (!(active instanceof HTMLElement)) {
    indicator.style.width = '0px';
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const activeRect = active.getBoundingClientRect();
  const left = activeRect.left - containerRect.left + container.scrollLeft;

  if (!animate) {
    indicator.style.transition = 'none';
  }

  indicator.style.left = `${left}px`;
  indicator.style.width = `${activeRect.width}px`;

  if (!animate) {
    // Flush the no-transition placement, then restore the CSS transition.
    void indicator.offsetWidth;
    indicator.style.transition = '';
  }

  if (scroll) scrollTriggerIntoView(container, left, activeRect.width, animate);
}

/**
 * Horizontally scrolls a tablist so the active trigger is fully visible, with a
 * little breathing room at each edge. No-op when the tablist doesn't overflow or
 * the trigger is already comfortably in view.
 * @param {HTMLElement} container - The (potentially) horizontally scrollable tablist.
 * @param {number} triggerLeft - Active trigger's left offset within the scroll content.
 * @param {number} triggerWidth - Active trigger's width.
 * @param {boolean} animate - Smooth-scroll when true, jump instantly when false.
 */
function scrollTriggerIntoView(container, triggerLeft, triggerWidth, animate) {
  const maxScroll = container.scrollWidth - container.clientWidth;
  if (maxScroll <= 0) return;

  const padding = 24;
  const viewStart = container.scrollLeft;
  const viewEnd = viewStart + container.clientWidth;
  const triggerRight = triggerLeft + triggerWidth;

  let target;
  if (triggerLeft < viewStart + padding) {
    target = triggerLeft - padding;
  } else if (triggerRight > viewEnd - padding) {
    target = triggerRight - container.clientWidth + padding;
  } else {
    return;
  }

  target = Math.max(0, Math.min(target, maxScroll));
  if (Math.abs(target - container.scrollLeft) < 1) return;

  container.scrollTo({ left: target, behavior: animate ? 'smooth' : 'auto' });
}

/** Repositions every indicator when layout changes (font load, width, etc.). */
const tabIndicatorObserver =
  typeof ResizeObserver === 'undefined'
    ? null
    : new ResizeObserver((entries) => {
        for (const entry of entries) positionTabIndicator(entry.target, { animate: false });
      });

function initTabGroups(root = document) {
  for (const group of root.querySelectorAll('[data-tabs]')) {
    if (!(group instanceof HTMLElement) || group.dataset.tabsInitialized === 'true') continue;

    group.dataset.tabsInitialized = 'true';
    new Tabs(group);
  }
}

/**
 * Wires indicator positioning for every `.inline-tab-navigation` container,
 * including standalone tablists not driven by [data-tabs]/[data-tab-scope].
 * @param {ParentNode} [root]
 */
function initTabIndicators(root = document) {
  for (const container of root.querySelectorAll(TAB_INDICATOR_CONTAINER_SELECTOR)) {
    if (!(container instanceof HTMLElement) || container.dataset.indicatorInit === 'true') continue;

    container.dataset.indicatorInit = 'true';
    tabIndicatorObserver?.observe(container);
    positionTabIndicator(container, { animate: false });
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  // Standalone inline tablists (triggers without [data-tab-button]) manage their
  // own active state; the [data-tab-button] ones reposition via the tab
  // controllers' #activateTab.
  const standalone = target.closest('.inline-tab-navigation-trigger:not([data-tab-button])');
  if (standalone instanceof HTMLElement) {
    const container = standalone.closest('.inline-tab-navigation');
    if (container instanceof HTMLElement) {
      for (const sibling of container.querySelectorAll('.inline-tab-navigation-trigger')) {
        sibling.classList.toggle('inline-tab-active', sibling === standalone);
      }
      positionTabIndicator(container);
    }
    return;
  }

  // Predictive-search type filter: predictive-search.js toggles
  // `search__type-active` on its own (inner) listener, which fires before this
  // bubbles to document, so the active class is already current here.
  const searchType = target.closest('.search__type');
  if (searchType instanceof HTMLElement) {
    const container = searchType.closest('.predictive-search-form__types');
    if (container instanceof HTMLElement) positionTabIndicator(container);
  }
});

// Treatment detail sidebar tabs: the standalone handler above already toggles the
// active class + indicator on the trigger; this swaps the matching panel by data-id,
// scoped to the trigger's own `.treatment-detailed-sidebar`.
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-detail-tab-trigger]') : null;
  if (!(trigger instanceof HTMLElement)) return;

  const sidebar = trigger.closest('.treatment-detailed-sidebar');
  if (sidebar instanceof HTMLElement) activateDetailTab(sidebar, trigger.dataset.id);
});

// Detail-item video play/pause: the video starts paused (no autoplay) and only plays
// on this interaction. Scoped to the button's own `.treatment-detail-item__media`.
document.addEventListener('click', (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-detail-video-toggle]') : null;
  if (!(btn instanceof HTMLElement)) return;

  const media = btn.closest('.treatment-detail-item__media');
  const video = media instanceof HTMLElement ? media.querySelector('video') : null;
  if (!(video instanceof HTMLVideoElement)) return;

  const paused = !video.paused;
  if (paused) {
    video.pause();
  } else {
    video.play();
  }
  syncDetailVideoToggle(media, paused);
});

const initializedScopes = new Set();

/**
 * @param {ParentNode} root
 * @param {string} sectionSelector
 * @param {string} drawerHostSelector
 * @param {string} sourceSelector
 * @param {string} cardSelector
 */
function moveCardDrawers(root, sectionSelector, drawerHostSelector, sourceSelector, cardSelector) {
  const sections = Array.from(root.querySelectorAll(sectionSelector)).filter(
    (section) => section instanceof HTMLElement
  );

  for (const section of sections) {
    const drawerHost = section.querySelector(drawerHostSelector);
    if (!(drawerHost instanceof HTMLElement)) continue;

    for (const source of section.querySelectorAll(sourceSelector)) {
      const cardHandle = source.closest(cardSelector)?.dataset.drawerHandle;

      while (source.firstElementChild) {
        const drawer = source.firstElementChild;
        if (cardHandle && drawer instanceof HTMLElement) {
          drawer.dataset.sidebarBody = cardHandle;
        }
        drawerHost.appendChild(drawer);
      }

      source.remove();
    }
  }
}

/**
 * @param {ParentNode} [root]
 */
function initEventCardDrawers(root = document) {
  moveCardDrawers(root, '[data-event-cards]', '.event-cards__drawers', '.event-card__drawers', '.event-card-item');
}

const DRAWER_SECTION_SELECTOR =
  '[data-treatment-cards], [data-location-cards], [data-event-cards], [data-uc-routine], [data-uc-highlight]';
const DRAWER_HOST_SELECTOR =
  '.card-slider__drawers, .location-cards__drawers, .event-cards__drawers, .routine__drawers, .product-highlight__drawers';

/**
 * Releases the media a mounted drawer set holds. A `<video>` keeps its buffered
 * data and decoder alive after the element is dropped from the DOM unless its
 * sources are cleared and it is reloaded.
 * @param {HTMLElement} host
 */
function releaseDrawerMedia(host) {
  for (const video of host.querySelectorAll('video')) {
    if (!(video instanceof HTMLVideoElement)) continue;

    video.pause();
    video.removeAttribute('src');
    for (const source of video.querySelectorAll('source')) source.removeAttribute('src');
    video.load();
  }
}

/**
 * Mounts one card's drawers into its section's single drawer host, replacing
 * whichever card's drawers were mounted before it, and stamps each wrapper's
 * `data-sidebar-body` with the card handle so `sidebars.js` can resolve it.
 *
 * Cards park their drawer markup in an inert `<template>` (see
 * `snippets/card-drawers.liquid`) rather than rendering it live, so a section
 * costs one sidebar instead of one per card — a slider of eleven treatments,
 * each with a hero image, an image grid, and detail-tab videos, was exhausting
 * memory on phones.
 *
 * @param {string} handle - The card's `data-open-drawer` / block id
 * @returns {boolean} whether the handle's drawers are now mounted
 */
function mountCardDrawers(handle) {
  const template = document.querySelector(`template[data-drawer-template="${CSS.escape(handle)}"]`);
  if (!(template instanceof HTMLTemplateElement)) return false;

  const section = template.closest(DRAWER_SECTION_SELECTOR);
  const host = section instanceof HTMLElement ? section.querySelector(DRAWER_HOST_SELECTOR) : null;
  if (!(host instanceof HTMLElement)) return false;

  if (host.dataset.mountedDrawer === handle) return true;

  releaseDrawerMedia(host);
  host.replaceChildren(template.content.cloneNode(true));
  host.dataset.mountedDrawer = handle;

  for (const wrapper of host.children) {
    if (wrapper instanceof HTMLElement) wrapper.dataset.sidebarBody = handle;
  }

  initTreatmentDetailSidebarTabs(host);
  initTabGroups(host);
  initTabIndicators(host);

  // The wrappers mount without the active class, and `sidebars.js` adds it later
  // in this same click. Resolve their styles now so the open transition has a
  // start value to animate from instead of snapping open.
  void host.offsetHeight;

  return true;
}

/**
 * @param {Element | null} target
 */
function mountDrawersForTrigger(target) {
  const trigger = target instanceof Element ? target.closest('[data-open-drawer]') : null;
  if (!(trigger instanceof HTMLElement) || !trigger.dataset.openDrawer) return;

  mountCardDrawers(trigger.dataset.openDrawer);
}

// Capture phase, so the clicked card's drawers are in the DOM before the
// document-level (bubbling) handlers in `sidebars.js` look them up by handle.
document.addEventListener('click', (event) => mountDrawersForTrigger(event.target), true);

document.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    mountDrawersForTrigger(event.target);
  },
  true
);

/**
 * Lifts treatment-card sidebar drawers out of their Swiper slides (which clip
 * via `overflow: hidden`) into a section-level host, rewiring each drawer's
 * `data-sidebar-body` to the owning card handle. Only the theme editor renders
 * those drawers live; on the storefront they arrive as templates and are mounted
 * on demand by `mountCardDrawers`, so this finds nothing to move.
 * @param {ParentNode} [root]
 */
function initTreatmentCardDrawers(root = document) {
  moveCardDrawers(
    root,
    '[data-treatment-cards]',
    '.card-slider__drawers',
    '.treatment-card__drawers',
    '.swiper-slide'
  );
}

/**
 * Reflects a detail-item video's play/paused state on its toggle button: swaps the
 * play/pause icons and the aria-label. Detail videos start paused and only play on
 * interaction, so "paused" shows the play icon.
 * @param {HTMLElement} scope - element containing the toggle (the media or its panel)
 * @param {boolean} paused
 */
function syncDetailVideoToggle(scope, paused) {
  const btn = scope.querySelector('[data-detail-video-toggle]');
  if (!(btn instanceof HTMLElement)) return;

  const playIcon = btn.querySelector('.treatment-detail-item__video-icon--play');
  const pauseIcon = btn.querySelector('.treatment-detail-item__video-icon--pause');
  if (playIcon instanceof HTMLElement) playIcon.classList.toggle('hidden', !paused);
  if (pauseIcon instanceof HTMLElement) pauseIcon.classList.toggle('hidden', paused);
  btn.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
}

function pauseDetailVideos(scope) {
  for (const media of scope.querySelectorAll('.treatment-detail-item__media')) {
    if (!(media instanceof HTMLElement)) continue;

    const video = media.querySelector('video');
    if (video instanceof HTMLVideoElement && !video.paused) video.pause();
    syncDetailVideoToggle(media, true);
  }
}

document.addEventListener('sidebar:close', (event) => {
  if (event.target instanceof HTMLElement) pauseDetailVideos(event.target);
});

/**
 * Sets the active treatment-detail tab within a single sidebar: highlights the
 * matching trigger and reveals only the panel whose data-id matches. Scoped to
 * one `.treatment-detailed-sidebar` so the many per-card sidebars never cross-talk.
 * Videos in deactivated panels are paused (and their toggle reset) so nothing plays
 * behind a hidden tab.
 * @param {HTMLElement} sidebar
 * @param {string | undefined} id
 */
function activateDetailTab(sidebar, id) {
  if (!id) return;

  for (const trigger of sidebar.querySelectorAll('[data-detail-tab-trigger]')) {
    if (trigger instanceof HTMLElement) trigger.classList.toggle('inline-tab-active', trigger.dataset.id === id);
  }

  for (const panel of sidebar.querySelectorAll('[data-detail-tab-body]')) {
    if (!(panel instanceof HTMLElement)) continue;

    const isActive = panel.dataset.id === id;
    panel.hidden = !isActive;

    if (!isActive) pauseDetailVideos(panel);
  }

  const nav = sidebar.querySelector('[data-detail-tab-nav]');
  if (nav instanceof HTMLElement) positionTabIndicator(nav);
}

/**
 * Relocates each detail-item trigger (`[data-detail-tab-trigger]`) out of the
 * sidebar body and into the heading's inline tab navigation (`[data-detail-tab-nav]`),
 * preserving document order, then activates the first tab. Trigger↔panel pairing is
 * by data-id, and everything is scoped per `.treatment-detailed-sidebar`, so a card's
 * tabs only ever control that card's panels.
 * @param {ParentNode} [root]
 */
function initTreatmentDetailSidebarTabs(root = document) {
  const scope = root instanceof Element || root instanceof Document ? root : document;
  const sidebars = new Set(
    Array.from(scope.querySelectorAll('.treatment-detailed-sidebar')).filter((el) => el instanceof HTMLElement)
  );
  if (scope instanceof HTMLElement && scope.classList.contains('treatment-detailed-sidebar')) sidebars.add(scope);

  for (const sidebar of sidebars) {
    if (sidebar.dataset.detailTabsInit === 'true') continue;

    const nav = sidebar.querySelector('[data-detail-tab-nav]');
    const triggers = Array.from(sidebar.querySelectorAll('[data-detail-tab-trigger]')).filter(
      (el) => el instanceof HTMLElement
    );
    if (!(nav instanceof HTMLElement) || !triggers.length) continue;

    for (const trigger of triggers) nav.appendChild(trigger);

    sidebar.dataset.detailTabsInit = 'true';
    activateDetailTab(sidebar, triggers[0].dataset.id);
    positionTabIndicator(nav, { animate: false });
  }
}

/**
 * @param {ParentNode} [root]
 */
function initLocationCardDrawers(root = document) {
  moveCardDrawers(
    root,
    '[data-location-cards]',
    '.location-cards__drawers',
    '.location-card__drawers',
    '.location-card'
  );
}

/**
 * @param {HTMLElement} container
 * @param {Iterable<Element>} items
 */
function appendClonedItemsByPlacementOrder(container, items) {
  /** @type {{ order: number, index: number, node: HTMLElement }[]} */
  const orderedItems = [];
  /** @type {HTMLElement[]} */
  const unorderedItems = [];
  let collectIndex = 0;

  for (const item of items) {
    if (!(item instanceof HTMLElement)) continue;

    const clone = /** @type {HTMLElement} */ (item.cloneNode(true));

    // `mountCardDrawers` resolves a handle document-wide, so a clone's copy of
    // the drawer template is dead weight.
    for (const template of clone.querySelectorAll('template[data-drawer-template]')) template.remove();

    const placementOrder = clone.dataset.placementOrder;
    const order = placementOrder ? Number.parseInt(placementOrder, 10) : Number.NaN;

    if (placementOrder && !Number.isNaN(order)) {
      orderedItems.push({ order, index: collectIndex, node: clone });
    } else {
      unorderedItems.push(clone);
    }

    collectIndex += 1;
  }

  orderedItems.sort((a, b) => a.order - b.order || a.index - b.index);

  for (const { node } of orderedItems) {
    container.appendChild(node);
  }

  for (const node of unorderedItems) {
    container.appendChild(node);
  }
}

/**
 * @param {ParentNode} root
 * @param {string} sectionSelector
 * @param {string} allGridSelector
 * @param {string} regionGridSelector
 */
function populateScopedAllPanel(root, sectionSelector, allGridSelector, regionGridSelector) {
  const sections = Array.from(root.querySelectorAll(sectionSelector)).filter(
    (section) => section instanceof HTMLElement
  );

  for (const section of sections) {
    const allGrid = section.querySelector(allGridSelector);
    if (!(allGrid instanceof HTMLElement)) continue;

    const allPanel = section.querySelector('[data-tab-body][data-id="all"]');
    const tabScope = allPanel instanceof HTMLElement ? allPanel.dataset.tabScope : undefined;
    if (!tabScope) continue;

    allGrid.replaceChildren();

    const regionPanels = section.querySelectorAll('[data-tab-body][data-id]:not([data-id="all"])');
    /** @type {HTMLElement[]} */
    const allItems = [];

    for (const panel of regionPanels) {
      if (!(panel instanceof HTMLElement) || panel.dataset.tabScope !== tabScope) continue;

      const grid = panel.querySelector(regionGridSelector);
      if (!(grid instanceof HTMLElement)) continue;

      for (const item of grid.children) {
        if (item instanceof HTMLElement) allItems.push(item);
      }
    }

    appendClonedItemsByPlacementOrder(allGrid, allItems);
  }
}

/**
 * @param {ParentNode} [root]
 */
function populateLocationAllPanels(root = document) {
  initLocationCardDrawers(root);
  populateScopedAllPanel(root, '[data-location-cards]', '[data-location-all-grid]', '.location-cards__grid');
}

/**
 * @param {ParentNode} [root]
 */
function populateEventAllPanels(root = document) {
  initEventCardDrawers(root);
  populateScopedAllPanel(root, '[data-event-cards]', '[data-event-all-grid]', '[data-event-cards-grid]');
}

/**
 * Resolves the currently active tab id for a scope, falling back to the default
 * ("all") tab when no tab has been activated yet.
 * @param {string} scope
 */
function getActiveTabId(scope) {
  const activeTab = document.querySelector(`[data-tab-scope="${scope}"][data-tab-button].active`);
  if (activeTab instanceof HTMLElement && activeTab.dataset.id) return activeTab.dataset.id;

  const defaultTab = document.querySelector(`[data-tab-scope="${scope}"][data-tab-button][data-tab-default]`);
  if (defaultTab instanceof HTMLElement && defaultTab.dataset.id) return defaultTab.dataset.id;

  return 'all';
}

/**
 * Counts the `.tab-body-item` elements belonging to the active tab's panel.
 * The "all" panel holds clones of every region item, so counting the active
 * panel yields the total on the All tab and the region total on a region tab.
 * @param {string} sectionSelector
 */
function countActiveTabItems(sectionSelector) {
  let count = 0;

  for (const section of document.querySelectorAll(sectionSelector)) {
    if (!(section instanceof HTMLElement)) continue;

    const allPanel = section.querySelector('[data-tab-body][data-id="all"]');
    const tabScope = allPanel instanceof HTMLElement ? allPanel.dataset.tabScope : undefined;
    if (!tabScope) continue;

    const activeId = getActiveTabId(tabScope);

    for (const panel of section.querySelectorAll(`[data-tab-body][data-id="${activeId}"]`)) {
      if (!(panel instanceof HTMLElement) || panel.dataset.tabScope !== tabScope) continue;
      count += panel.querySelectorAll('.tab-body-item').length;
    }
  }

  return count;
}

function updateTabItemCount() {
  const countEl = document.getElementById('tab-item-count');
  if (!(countEl instanceof HTMLElement)) return;

  const count = countActiveTabItems('[data-event-cards]') + countActiveTabItems('[data-location-cards]');

  countEl.textContent = `(${count})`;
}

function hydrateTabs() {
  populateLocationAllPanels();
  populateEventAllPanels();
  initTreatmentCardDrawers();
  initTreatmentDetailSidebarTabs();
  updateTabItemCount();
  initTabGroups();

  const scopes = new Set(
    Array.from(document.querySelectorAll('[data-tab-scope]'))
      .map((element) => element.dataset.tabScope)
      .filter(Boolean)
  );

  for (const scope of scopes) {
    if (initializedScopes.has(scope)) continue;

    initializedScopes.add(scope);
    new ScopedTabs(scope);
  }

  initTabIndicators();
}

// Trigger labels can reflow once webfonts load, changing trigger widths.
if (document.fonts?.ready) {
  document.fonts.ready.then(() => {
    for (const container of document.querySelectorAll(TAB_INDICATOR_CONTAINER_SELECTOR)) {
      positionTabIndicator(container, { animate: false });
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hydrateTabs);
} else {
  hydrateTabs();
}

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof HTMLElement)) return;

  populateLocationAllPanels(event.target);
  populateEventAllPanels(event.target);
  initTreatmentCardDrawers(event.target);
  initTreatmentDetailSidebarTabs(event.target);
  updateTabItemCount();
  initTabIndicators(event.target);
});
